import { ReactNode, useCallback, useRef, useState } from "react";
import { ScheduleSourceMove } from "@common/helpers/domains/accounting/scheduleSourceMoves";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { scheduleSourceMoveConfirmCopy } from "./scheduleSourceMoveCopy";

/**
 * Confirms moving a payment schedule onto the document before the match is
 * written. Cancel leaves both records alone.
 */
export function useScheduleSourceMoveConfirm(): {
    runScheduleSourceMoveConfirm: (
        moves: ScheduleSourceMove[],
    ) => Promise<boolean>;
    scheduleSourceMoveConfirm: ReactNode;
} {
    const [moves, setMoves] = useState<ScheduleSourceMove[] | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const pendingResolveRef = useRef<((confirmed: boolean) => void) | null>(
        null,
    );

    const finish = useCallback((confirmed: boolean) => {
        pendingResolveRef.current?.(confirmed);
        pendingResolveRef.current = null;
        setIsOpen(false);
    }, []);

    const runScheduleSourceMoveConfirm = useCallback(
        (nextMoves: ScheduleSourceMove[]) =>
            new Promise<boolean>((resolve) => {
                pendingResolveRef.current?.(false);
                pendingResolveRef.current = resolve;
                setMoves(nextMoves);
                setIsOpen(true);
            }),
        [],
    );

    const documentType = moves?.every(
        (move) => move.documentType === ScheduleSourceType.invoice,
    )
        ? ScheduleSourceType.invoice
        : ScheduleSourceType.bill;
    const copy = scheduleSourceMoveConfirmCopy(documentType);

    return {
        runScheduleSourceMoveConfirm,
        scheduleSourceMoveConfirm: (
            <ConfirmationModal
                show={isOpen}
                title={copy.title}
                question={copy.body}
                yes={copy.confirm}
                no={copy.cancel}
                onConfirm={() => finish(true)}
                onReject={() => finish(false)}
                onHide={() => finish(false)}
                onExited={() => setMoves(null)}
            />
        ),
    };
}
